import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { NotificationItem, NotificationResponse, NotificationType } from 'src/app/model/Notification';
import { DataTableService } from 'src/app/service/data-table.service';
import { EventService } from 'src/app/service/event.service';
import { GeneralService } from 'src/app/service/general.service';
import { Events } from 'src/app/service/websocket.service';
import { Utils } from 'src/app/utils';
import { ActionMenuEvent, ActionMenuIcon, ActionMenuItem } from '../controls/actionmenu/action-menu.component';
import { ColumnSort, ColumnType, DataTableColumn, DataTableFilter, DataTableFilterOption, DataTableRow, FilterType } from '../controls/datatable/datatable.component';
import { ModalMessageComponent } from '../controls/modal-message/modal-message.component';
import { Modal } from '../controls/modal/modal.service';
import { PageCount, PageEvent } from '../controls/pagination/pagination.component';
import { AuthenticationService } from 'src/app/service/authentication.service';
import { HeaderComponent } from '../header/header.component';
import { BaseComponent } from 'src/app/model/BaseComponent';
import { SolutionService } from 'src/app/service/solution.service';

export enum NotificationField {
  TYPE = "messageType"
}

@Component({
    selector: 'app-activities-notifications',
    templateUrl: './activities-notifications.component.html',
    styleUrls: ['./activities-notifications.component.css'],
    standalone: false
})
export class ActivitiesNotificationsComponent extends BaseComponent implements OnInit {

  cols: Array<DataTableColumn>;
  rows: DataTableRow[] = [];
  notificationResponse: NotificationResponse;
  total: number = 0;
  itemsPerPage: number = PageCount.TWENTYFIVE;
  filters: DataTableFilter[] = [];
  filterOpen: boolean = false;
  filterApplied: boolean = false;
  notificationType: string = "";
  eventMap: Map<string, DataTableFilterOption> = new Map<string, DataTableFilterOption>();
  header:HeaderComponent;

  solutionInfoSubscription: Subscription;
  filterSubscription: Subscription;
  applyFilterSubscription: Subscription;
  cancelFilterSubscription: Subscription;

  constructor(private generalService: GeneralService, protected eventService: EventService, 
              private dataTableSrv: DataTableService, protected modal: Modal, private router: Router,
              protected authService:AuthenticationService, protected solutionService: SolutionService) { 
      super(eventService, modal, authService, solutionService);
      this.solutionInfoSubscription = this.eventService.currentSolutionInfo$.subscribe(newSolutionInfo => {
        this.solutionInfo = newSolutionInfo;
        this.title = `${this.solutionInfo.currentEnvironment.solName} : ${this.solutionInfo.currentEnvironment.name}`;
        this.getNotifications(0);
      });

      this.filterSubscription = this.dataTableSrv.filterClicked$.subscribe(() => {
        this.filterOpen = !this.filterOpen;
        if (!this.filterOpen && this.filterApplied) {
          this.cancelFilter();
        }
      })

      this.applyFilterSubscription = this.dataTableSrv.applyFilter$.subscribe((appliedFilters: DataTableFilter[]) => {
        appliedFilters.forEach(appliedFilter => {
          switch (appliedFilter.field) {
            case NotificationField.TYPE: {
              if (appliedFilter.selected !== "") {
                this.notificationType = appliedFilter.selected;
              }else{
                this.notificationType = "";
              }
              break;
            }
          }
          appliedFilter.field
        });
        this.getNotifications(0);
        this.filterApplied = true;
      });

      this.cancelFilterSubscription = this.dataTableSrv.cancelFilter$.subscribe(() => {
        this.cancelFilter();
      });
  }

  ngOnInit(): void {
    this.currentUserAuthInfo = this.authService.getLoggedInUser();
    this.cols = Array<DataTableColumn>(
      new DataTableColumn("text", "Type", ColumnType.STRING, false, "18"),
      new DataTableColumn("messageData", "Message", ColumnType.STRING, false, "67"),
      new DataTableColumn("timestamp", "Date", ColumnType.DATE, true, "15", ColumnSort.DESC)
    );
  }

  ngOnDestroy() {
    this.solutionInfoSubscription.unsubscribe();
    this.filterSubscription.unsubscribe();
    this.applyFilterSubscription.unsubscribe();
    this.cancelFilterSubscription.unsubscribe();
  }

  cancelFilter(): void {
    this.notificationType = "";
    this.itemsPerPage = PageCount.TWENTYFIVE;
    this.dataTableSrv.onResetPagination();
    this.getNotifications(0);
    this.filterApplied = false;
  }

  getNotifications(startIndex: number): void {
    this.loading = true;
    this.generalService.getNotificationPageDetails(this.notificationType, this.itemsPerPage, startIndex).pipe(
      tap(resp => {
        this.notificationResponse = resp.body;
        this.total = this.notificationResponse.count;
        if(this.filters.length == 0){
          this.eventMap = Utils.createNotificationEventMap(this.notificationResponse.filters);
          this.filters = Array<DataTableFilter>(
            new DataTableFilter("audit-filter-event", NotificationField.TYPE, $localize`Type`, FilterType.DROPDOWN, this.formatEventOptions(this.notificationResponse.filters)),
          );
        }
        this.formatNotifications();
      }),
      finalize(() => {
        this.loading = false;
      })
    ).subscribe();
  }

  onPageChange(pageEvent: PageEvent) {
    this.itemsPerPage = pageEvent.itemsPerPage;
    this.getNotifications(pageEvent.startingIndex);
  }

  formatEventOptions(auditEvents: string[]): DataTableFilterOption[] {
    var eventOptions: DataTableFilterOption[] = [];

    auditEvents.forEach(auditEvent => {
      eventOptions.push(this.eventMap.get(auditEvent));
    });
    eventOptions.sort((a, b) => (a.text > b.text ? 1 : -1));
    eventOptions.unshift(new DataTableFilterOption($localize`All events`, ""));

    return eventOptions;
  }

  actionMenuClicked(event: ActionMenuEvent): void {
    if(event.name == "Delete"){
      const sub: Subscription =
        this.modal.open(ModalMessageComponent, {
          data: {
            title: $localize`Remove`,
            message: $localize`${Utils.plural(event.data.length,
              {
                1: $localize`1 notification`,
                other: $localize`${event.data.length} notifications`
              })} will be permanently removed. Remove selected notification?`,
            primaryBtn: $localize`Remove`,
            secondaryBtn: $localize`Cancel`,
            width: '350px'
          },
          id: 'otsc-notifications-remove-msg'
        }
        ).afterClosed.subscribe((isPrimaryBtnClicked: boolean) => {
          if (isPrimaryBtnClicked) {
            let notifications: number[] = [];
            event.data.forEach((data) => {
              notifications.push(data.id);
            });

            this.generalService.deleteNotifications(notifications).pipe(
              tap(resp => {
                this.getNotifications(0);
                Utils.addHeaderMessageEventBanner(this.eventService, Events.SuccessMsg,
                  $localize`${Utils.plural(event.data.length,
                    {
                      1: $localize`1 notification was`,
                      other: $localize`${event.data.length} notifications were`
                    })} removed successfully`);
              })
            ).subscribe();
          }
          sub.unsubscribe();
        });
    }
  }

  formatNotifications(): void {
    var tmpRows: DataTableRow[] = [];
    this.rows = [];
    this.notificationResponse.notifications.forEach((item) => {
      var tmpNotification: NotificationItem = new NotificationItem();
      tmpNotification.id = item.id;
      tmpNotification.timestamp = item.timestamp;
      switch (item.type) {
        case NotificationType.REGISTER: {
          tmpNotification.messageData = $localize`System waiting for approval: ${item.messageData["name"]}`;
          break;
        }
        case NotificationType.APPROVE: {
          tmpNotification.messageData = $localize`System approved: ${item.messageData["name"]}`;
          break;
        }
        case NotificationType.ONLINE: {
          tmpNotification.messageData = $localize`System online: ${item.messageData["name"]}`;
          break;
        }
        case NotificationType.OFFLINE: {
          tmpNotification.messageData = $localize`System offline: ${item.messageData["name"]}`;
          break;
        }
        case NotificationType.DISCOVERY: {
          tmpNotification.messageData = $localize`Discover products complete: ${item.messageData["name"]}`;
          break;
        }
        case NotificationType.ENABLE_PRODUCT: {
          tmpNotification.messageData = $localize`Enable component instance: ${item.messageData["name"]}`;
          break;
        }
        case NotificationType.PATCH_INSTALLATION:  {
          tmpNotification.messageData = $localize`Patch installation complete: ${Utils.plural(item.messageData["systemCount"], 
                                                                                                  {1: $localize`1 System`, 
                                                                                                   other: $localize`${item.messageData["systemCount"]} Systems`})}
                                                                                            (${Utils.plural(item.messageData["instanceCount"], 
                                                                                                  {1: $localize`1 Instance`, 
                                                                                                  other: $localize`${item.messageData["instanceCount"]} Instances`})})`;
          break;
        }
        case NotificationType.BRING_UP_TO_DATE:  {
          tmpNotification.messageData = $localize`Bring up to date complete: ${Utils.plural(item.messageData["systemCount"], 
                                                                                                  {1: $localize`1 System`, 
                                                                                                   other: $localize`${item.messageData["systemCount"]} Systems`})}
                                                                                            (${Utils.plural(item.messageData["instanceCount"], 
                                                                                                  {1: $localize`1 Instance`, 
                                                                                                  other: $localize`${item.messageData["instanceCount"]} Instances`})})`;
          break;
        }
        case NotificationType.SYNC_PATCH_INSTALLATION:  {
          tmpNotification.messageData = $localize`Synchronize Patch installation complete: ${Utils.plural(item.messageData["systemCount"], 
                                                                                                  {1: $localize`1 System`, 
                                                                                                   other: $localize`${item.messageData["systemCount"]} Systems`})}
                                                                                            (${Utils.plural(item.messageData["instanceCount"], 
                                                                                                  {1: $localize`1 Instance`, 
                                                                                                  other: $localize`${item.messageData["instanceCount"]} Instances`})})`;
          break;
        }
        case NotificationType.REMOVE: {
          tmpNotification.messageData = $localize`System removed: ${item.messageData["name"]}`;
          break;
        }
        case NotificationType.DISAPPROVE: {
          tmpNotification.messageData = $localize`System disapproved: ${item.messageData["name"]}`;
          break;
        }
        case NotificationType.ENABLE: {
          tmpNotification.messageData = $localize`System enabled: ${item.messageData["name"]}`;
          break;
        }
        case NotificationType.DISABLE: {
          tmpNotification.messageData = $localize`System disabled: ${item.messageData["name"]}`;
          break;
        }
        case NotificationType.AGENT_MANIFEST_ERROR: {
          tmpNotification.messageData = $localize`System: ${item.messageData["hostname"]}`;
          break;
        }
        case NotificationType.SOFTWARE_ROLLBACK: {
          tmpNotification.messageData = $localize`Software rollback complete: ${Utils.plural(item.messageData["systemCount"], 
                                                                                        {1: $localize`1 System`, 
                                                                                         other: $localize`${item.messageData["systemCount"]} Systems`})}
                                                                                (${Utils.plural(item.messageData["instanceCount"], 
                                                                                        {1: $localize`1 Instance`, 
                                                                                         other: $localize`${item.messageData["instanceCount"]} Instances`})})`;
          break;
        }
        case NotificationType.PACKAGE_LOGS:{
          tmpNotification.messageData = (item.messageData.isSuccess) ? $localize`The requested logs are ready to be downloaded.` :
            $localize`The system failed to package the requested logs.`;
            break;
        }
        case NotificationType.PACKAGE_PROD_LOGS:{
          tmpNotification.messageData = (item.messageData.isSuccess) ? $localize`The requested product logs are ready to be downloaded.` :
            $localize`The system failed to package the requested product logs.`;
            break;
        }
        case NotificationType.ADMIN_SETTINGS:{
          tmpNotification.messageData = item.messageData.isSuccess ? $localize`The system applied administration settings successfully. (id: ${item.messageData["activityId"]})`: `The system failed to apply administration settings. (id: ${item.messageData["activityId"]})`;
          break;
        }
        case NotificationType.REMOVE_PATCHES:{
          tmpNotification.messageData = $localize`Patch removal complete: ${Utils.plural(item.messageData["systemCount"], 
                                                                                        {1: $localize`1 System`, 
                                                                                          other: $localize`${item.messageData["systemCount"]} Systems`})}
                                                                                  (${Utils.plural(item.messageData["instanceCount"], 
                                                                                        {1: $localize`1 Instance`, 
                                                                                        other: $localize`${item.messageData["instanceCount"]} Instances`})})`;
          break;
        }
      }
      var actions = new Array<ActionMenuItem>(
        new ActionMenuItem("Delete", $localize`Remove`, ActionMenuIcon.DELETE, tmpNotification, true)
      );
      tmpNotification.text = this.eventMap.get(item.type).text;
      tmpRows.push(new DataTableRow(tmpNotification, actions));
    });

    this.rows = tmpRows;
  }

  goToNotificationSettings(): void{
    this.router.navigate(['/settings/notifications']);
  }
}